// src/data/schemas/GasStation.js
// Gas station status — community-reported and verified.
// Firestore collection: gas_stations

import { uuid, nowISO, isLat, isLon, isISO, isIn, runValidation, toFirestoreClean, fromFirestoreNormalize } from './shared';

export const GAS_STATUS     = ['open', 'busy', 'closed', 'unknown'];
export const GAS_FUEL_TYPES = ['regular', 'midgrade', 'premium', 'diesel', 'e85'];
export const GAS_PAYMENT    = ['cash_only', 'card_only', 'both', 'unknown'];

export const SCHEMA_GAS_STATION = {
  id:            { required: true,  type: 'string' },
  name:          { required: true,  type: 'string', maxLen: 200 },
  brand:         { required: false, type: 'string', maxLen: 100 },
  address:       { required: true,  type: 'string', maxLen: 300 },
  zone:          { required: true,  type: 'string', maxLen: 100 },
  parish:        { required: false, type: 'string', maxLen: 100 },
  lat:           { required: true,  type: 'number', check: isLat, message: 'valid latitude' },
  lon:           { required: true,  type: 'number', check: isLon, message: 'valid longitude' },
  status:        { required: true,  type: 'string', check: (v) => isIn(v, GAS_STATUS),     message: `one of: ${GAS_STATUS.join(', ')}` },
  fuelTypes:     { required: false, type: 'array'  },                                          // GAS_FUEL_TYPES[]
  priceRegular:  { required: false, type: 'number', min: 0, max: 20 },                        // USD per gallon
  paymentType:   { required: false, type: 'string', check: (v) => isIn(v, GAS_PAYMENT) },
  waitMinutes:   { required: false, type: 'number', min: 0, max: 480 },
  hasPower:      { required: false, type: 'boolean' },   // generator/power available
  hasWater:      { required: false, type: 'boolean' },   // drinking water available
  reportedAt:    { required: true,  type: 'string', check: isISO, message: 'valid ISO datetime' },
  reportedBy:    { required: false, type: 'string', maxLen: 100 },
  verifiedAt:    { required: false, type: 'string', check: isISO, message: 'valid ISO datetime' },
  confirmCount:  { required: false, type: 'number', min: 0 },
  denyCount:     { required: false, type: 'number', min: 0 },
};

export function createGasStation(overrides = {}) {
  return {
    id:           uuid(),
    name:         '',
    brand:        null,
    address:      '',
    zone:         '',
    parish:       null,
    lat:          null,
    lon:          null,
    status:       'unknown',
    fuelTypes:    [],
    priceRegular: null,
    paymentType:  'unknown',
    waitMinutes:  null,
    hasPower:     null,
    hasWater:     null,
    reportedAt:   nowISO(),
    reportedBy:   null,
    verifiedAt:   null,
    confirmCount: 0,
    denyCount:    0,
    ...overrides,
  };
}

export function validateGasStation(obj) {
  return runValidation(obj, SCHEMA_GAS_STATION);
}

// Migrate from static.js legacy shape
export function gasStationFromStatic(legacy, zone) {
  const price    = legacy.price ? parseFloat(legacy.price.replace('$', '')) : null;
  const waitMins = legacy.wait === 'No wait' ? 0
                 : legacy.wait === 'Closed'  ? null
                 : legacy.wait ? parseInt(legacy.wait.replace(/\D/g, ''), 10) || null : null;

  const fuel = (raw) => {
    if (!raw) return [];
    const s = raw.toLowerCase();
    const out = [];
    if (s.includes('all grade')) return ['regular','midgrade','premium'];
    if (s.includes('regular'))  out.push('regular');
    if (s.includes('midgrade')) out.push('midgrade');
    if (s.includes('premium'))  out.push('premium');
    if (s.includes('diesel'))   out.push('diesel');
    if (s.includes('e85'))      out.push('e85');
    return [...new Set(out)];
  };

  return createGasStation({
    id:           legacy.id,
    name:         legacy.name,
    brand:        legacy.name.split(' - ')[0] ?? null,
    address:      legacy.address,
    zone,
    lat:          legacy.lat ?? null,
    lon:          legacy.lon ?? null,
    status:       legacy.status,
    fuelTypes:    fuel(legacy.fuel),
    priceRegular: Number.isNaN(price) ? null : price,
    waitMinutes:  waitMins,
    reportedAt:   new Date(Date.now() - (legacy.updatedMins ?? 0) * 60000).toISOString(),
  });
}

const GAS_FS_KEYS = [
  'id','name','brand','address','zone','parish','lat','lon',
  'status','fuelTypes','priceRegular','paymentType','waitMinutes',
  'hasPower','hasWater',
  'reportedAt','reportedBy','verifiedAt','confirmCount','denyCount',
];

const GAS_MIGRATIONS = {
  // v0 had 'gasType' instead of 'fuelTypes' — rename it
  0: (d) => {
    if (d.gasType && !d.fuelTypes) {
      d.fuelTypes = Array.isArray(d.gasType) ? d.gasType : [d.gasType];
      delete d.gasType;
    }
    return d;
  },
};

export const GasStationConverter = {
  toFirestore:   (g) => toFirestoreClean(g, GAS_FS_KEYS),
  fromFirestore: (snap) => fromFirestoreNormalize(snap.data(), createGasStation, GAS_MIGRATIONS),
};
